import { useContext, useEffect, useCallback, useState, useRef } from 'react';
import { FabricContext } from '@context/FabricContext';
import { useSelectedMVComment } from '@hooks';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import useUserPicture from '@pages/MediaViewer/AssetViewer/Canvas/hooks/useUserPicture';
import { readyArrowCreator } from '@pages/MediaViewer/AssetViewer/Canvas/Objects/arrowHandlers';
import { readyBrushCreator } from '@pages/MediaViewer/AssetViewer/Canvas/Objects/brushHandlers';
import { readyEllipseCreator } from '@pages/MediaViewer/AssetViewer/Canvas/Objects/ellipseHandlers';
import { readyLineCreator } from '@pages/MediaViewer/AssetViewer/Canvas/Objects/lineHandlers';
import { readyPinCreator } from '@pages/MediaViewer/AssetViewer/Canvas/Objects/pinHandlers';
import { readyRectCreator } from '@pages/MediaViewer/AssetViewer/Canvas/Objects/rectHandlers';
import { readyTextCreator } from '@pages/MediaViewer/AssetViewer/Canvas/Objects/textHandlers';
import changeObjectPosition from '@pages/MediaViewer/AssetViewer/Canvas/utils/changeObjectsPosition';
import {
  fitObjectSize,
  fitPinSize
} from '@pages/MediaViewer/AssetViewer/Canvas/utils/fitObject';

export default function useCanvasHistory({
  assetType
}: {
  assetType:
    | 'audio'
    | 'video'
    | 'document'
    | 'raster_image'
    | 'camera_raw_image'
    | 'vector_image'
    | 'presentation'
    | 'spreadsheet'
    | undefined;
}) {
  const { setMyPicture } = useUserPicture();
  const { turnoverDegree, selectedCommentId } = useAssetViewerState();
  const selectedComment = useSelectedMVComment();
  const selectedCommentIdRrf = useRef<string>(selectedComment?.id || '');
  selectedCommentIdRrf.current = selectedComment?.id || '';
  const {
    clearCanvas,
    canvas,
    mouseOverObjectRef,
    objectToCreateRef,
    activeObjectRef
  } = useContext(FabricContext);
  const [history, setHistory] = useState<any>([]);
  const [mods, setMods] = useState<any>([]);

  useEffect(() => {
    setHistory([]);
    setMods(0);
  }, [selectedCommentId]);

  const updateHistory = useCallback(() => {
    if (!canvas) return;

    const json = canvas.toJSON(['assetAngle', 'userEmail', 'userId']) as any;
    const history = JSON.stringify(json.objects);

    setHistory((prev: any) => {
      return [...prev, { objects: history }];
    });
    setMods(0);
  }, [canvas]);

  useEffect(() => {
    if (!canvas) return;
    const listener = updateHistory;
    canvas.on('object:history:add', listener);
    canvas.on('object:history:remove', listener);
    canvas.on('object:history:modified', listener);
    return () => {
      canvas.off('object:history:add', listener);
      canvas.off('object:history:remove', listener);
      canvas.off('object:history:modified', listener);
    };
  }, [updateHistory, canvas, objectToCreateRef]);

  const handleObjectCreators = useCallback(
    (params) => {
      if (params.obj.type === 'rect') readyRectCreator(params);
      if (params.obj.type === 'brush') readyBrushCreator(params);
      if (params.obj.type === 'arrow') readyArrowCreator(params);
      if (params.obj.type === 'line') readyLineCreator(params);
      if (params.obj.type === 'ellipse') readyEllipseCreator(params);
      if (params.obj.type === 'i-text') readyTextCreator(params);
      if (params.obj.type === 'pin') {
        const { email, name, picture } = setMyPicture();
        readyPinCreator({
          ...params,
          userEmail: email,
          userName: name,
          userPicture: picture
        });
      }
    },
    [setMyPicture]
  );

  const handleSetAnnotations = useCallback(
    (objects: any) => {
      if (!canvas) return;
      const annotations = JSON.parse(objects);
      const strokeWidth = fitObjectSize(canvas, assetType);
      const pinScale = fitPinSize(canvas);
      annotations.forEach((obj: any) => {
        let _obj = obj as any;
        if (_obj.type === 'pin') {
          _obj = {
            ..._obj,
            strokeWidth
          };
        } else if (_obj.type === 'arrow') {
          const arrowScaleX = Math.abs(_obj.scaleX || 1);
          const arrowScaleY = Math.abs(_obj.scaleY || 1);
          _obj = {
            ..._obj,
            objects: [
              {
                ..._obj.objects[0],
                strokeUniform: true,
                strokeWidth
              },
              {
                ..._obj.objects[1],
                scaleX: strokeWidth / arrowScaleX,
                scaleY: strokeWidth / arrowScaleY
              }
            ]
          };
        } else {
          _obj = {
            ..._obj,
            strokeWidth
          };
        }
        const params = {
          obj: _obj,
          canModify: true,
          canvas,
          overObject: mouseOverObjectRef,
          scale: pinScale,
          callback: () =>
            changeObjectPosition({
              canvas,
              angle: turnoverDegree
            })
        };
        handleObjectCreators(params);
      });
      activeObjectRef.current = undefined;
    },
    [
      canvas,
      activeObjectRef,
      handleObjectCreators,
      mouseOverObjectRef,
      turnoverDegree,
      assetType
    ]
  );

  const redoHistory = useCallback(() => {
    if (!canvas) return;
    if (mods <= 0) return;

    const elem = history[history.length - 1 - mods + 1];
    clearCanvas();

    setMods((prev: number) => prev - 1);
    if (!elem) return;
    handleSetAnnotations(elem.objects);
  }, [canvas, clearCanvas, history, mods, handleSetAnnotations]);

  const undoHistory = useCallback(() => {
    if (!canvas) return;
    if (mods >= history.length) return;
    const elem = history[history.length - 1 - mods - 1];

    clearCanvas();

    setMods((prev: number) => prev + 1);
    if (!elem) return;

    handleSetAnnotations(elem.objects);
  }, [canvas, clearCanvas, handleSetAnnotations, history, mods]);

  return {
    history,
    mods,
    redoHistory,
    undoHistory
  };
}
